import { ApiResponse, PaginatedResponse } from '../types/api.types';
import {
  Asset,
  AssetCreateRequest,
  AssetFile,
  AssetSearchParams,
  AssetUpdateRequest,
  FileUpload,
  FileUploadResponse,
} from '../types/asset.types';
import {
  AssetDetailsResponse,
  AssetLayer,
  CompositeMetadataResponse,
  LookAssetUpdateRequest,
  LookMetadataResponse,
  MoveAssetUpdateRequest,
  MoveMetadataResponse,
  SongAssetUpdateRequest,
  SongMetadataDetailsResponse,
  StarAssetUpdateRequest,
  StarMetadataResponse,
  StoredAssetDTO,
  TagsResponse,
  WorldAssetUpdateRequest,
  WorldMetadataResponse,
} from '../contracts/openapi/assetContracts';
import api from '../http/axiosClient';
import { publicRequest } from '../http/request';
import {
  getBackendAvailability,
  setBackendAvailability,
} from '../http/backendStatus';
import assetRegistryService from './assetRegistryService';
// Import checkEnv only when needed - currently commented out in the code
// import { checkEnv } from './envCheck';
import { ContractEnforcer } from '../services/contractEnforcement';
import {
  getBackendUrl,
  getEnvironmentConfig,
  getUploadEndpoint
} from '../utils/environment.config';
import {
  normalizeHairColor,
} from '../utils/sharedVocabulary';
// Import V1.5.0 MFA conversion functions
import {
  convertHFNToMFA_V150
} from '../services/enhancedTaxonomyService';
// Import URL normalization service to fix environment mismatch CORS issues
import {
  normalizeApiResponse
} from '../services/urlNormalizationService';
// Import conditional logging utilities
import { conditionalLog, logger } from '../utils/logger';

// Retry mechanism for concurrency errors
const retryWithBackoff = async <T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 500,
  operationName: string = 'operation'
): Promise<T> => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error: any) {
      const isVersionConcurrencyError =
        error.response?.status === 500 &&
        error.response?.data?.message?.includes('Version snapshot failed');

      const isAssetBeingModifiedError =
        error.response?.status === 500 &&
        error.response?.data?.message?.includes('being modified by another process');

      if ((isVersionConcurrencyError || isAssetBeingModifiedError) && attempt < maxRetries) {
        const delay = baseDelay * Math.pow(2, attempt - 1); // Exponential backoff
        console.warn(`🔄 [RETRY ${attempt}/${maxRetries}] ${operationName} failed due to concurrency error. Retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }

      // If not a concurrency error or max retries reached, throw the error
      throw error;
    }
  }

  throw new Error(`Max retries (${maxRetries}) exceeded for ${operationName}`);
};

/** Layer code to API path segment for PUT /assets/{path}/{assetId} and PUT .../file. */
const LAYER_TO_PATH: Record<AssetLayer, string> = {
  G: 'song',
  S: 'star',
  L: 'look',
  M: 'move',
  W: 'world',
  B: 'brand',
  P: 'personalize',
  T: 'training',
  C: 'composite',
  R: 'rights',
};

// Determine whether real backend is available and connected
// Use the API module's backend status tracking
let isBackendAvailable = getBackendAvailability();
let lastBackendCheck = 0;
const BACKEND_CHECK_INTERVAL = 30 * 1000; // 30 seconds

// Simple check function to be run at startup
const checkBackendAvailability = async () => {
  try {
    // Always start assuming backend is unavailable until proven otherwise
    let realBackendWorking = false;

    // For caching - only recheck if it's been long enough since last check
    const now = Date.now();
    if (
      now - lastBackendCheck < BACKEND_CHECK_INTERVAL &&
      lastBackendCheck > 0
    ) {
      return isBackendAvailable;
    }

    // Try to hit our own health endpoint first - this just tells us if *our* API is working,
    // not the real backend
    try {
      // Health endpoint is at /health (not /api/v1/taxonomy/health)
      const url = `${getBackendUrl()}/version`;
      logger.debug(`Fetching health from: ${url}`);

      const healthResponse = await publicRequest<string>({
        url,
        responseType: 'text',
        validateStatus: () => true,
      });

      // If health endpoint works, log success but continue checking
      if (healthResponse.status >= 200 && healthResponse.status < 300) {

        // Try to get the response body for more debugging
        try {
          // First check if it's HTML
          const responseText = healthResponse.data ?? '';
          if (
            responseText.trim().startsWith('<!doctype html>') ||
            responseText.includes('<html')
          ) {
            // Log a more detailed error with visual emphasis
            console.error(`
======================================
API ROUTING CONFIGURATION ERROR DETECTED
======================================
The server is returning HTML instead of JSON for API endpoints.
This happens when the server is not properly configured to handle API routes.

This is common when using 'serve -s build' without proper configuration.

SOLUTION:
1. Use the provided serve-local.sh script: ./serve-local.sh
   OR
2. Use serve with the config file: serve -s build -l 3000 --config serve.json

The serve.json file contains special routing rules to handle API requests properly.
======================================`);

            // Set a flag in localStorage to show an error in the UI
            try {
              localStorage.setItem('apiRoutingError', 'true');
            } catch (e) {
              // Ignore localStorage errors
            }

            // This is definitely indicative of an issue
          } else {
            // Try to parse as JSON
            try {
              const healthBody = JSON.parse(responseText);
              // Check for our special marker
              if (
                healthBody &&
                healthBody.diagnostics &&
                healthBody.diagnostics.responseMarker === 'JSON_RESPONSE_MARKER'
              ) {
                // Clear any previous error flags
                try {
                  localStorage.removeItem('apiRoutingError');
                } catch (e) {
                  // Ignore localStorage errors
                }
              }
            } catch (jsonError) {
              // Could not parse health response as JSON
            }
          }
        } catch (parseError) {
          // Could not parse health response
        }
      }
    } catch (healthError) {
      // Health endpoint request failed
    }

    // Use the standard health endpoint to test backend connectivity
    try {
      const url = `${getBackendUrl()}/version`;
      logger.debug(`Fetching health from: ${url}`);

      const response = await publicRequest<string>({
        url,
        responseType: 'text',
        validateStatus: () => true,
      });

      if (response.status >= 200 && response.status < 300) {
        try {
          const responseText = response.data ?? '';
          if (responseText.trim().startsWith('<!doctype html>')) {
            // Since we got HTML, the backend is not working correctly
            realBackendWorking = false;
          } else {
            // Try to parse as JSON
            try {
              const healthResult = JSON.parse(responseText);
              // Check if backend reports healthy status
              realBackendWorking = healthResult.status === 'healthy';
            } catch (jsonError) {
              realBackendWorking = false;
            }
          }
        } catch (parseError) {
          realBackendWorking = false;
        }
      }
    } catch (testError) {
      // Health endpoint request failed
    }

    isBackendAvailable = realBackendWorking;
    setBackendAvailability(isBackendAvailable);

    // Update cache timestamp
    lastBackendCheck = now;

    // Also update the API module's status for consistency
    if (isBackendAvailable !== getBackendAvailability()) {
      setBackendAvailability(isBackendAvailable);
      // Expose for debugging
      try {
        (window as any).apiBackendAvailable = isBackendAvailable;
      } catch (e) {
        // Ignore if window is not defined (e.g., in test environment)
      }
    }

    return isBackendAvailable;
  } catch (error) {
    console.error('Backend availability check failed:', error);
    isBackendAvailable = false;
    setBackendAvailability(false);
    lastBackendCheck = Date.now(); // Update timestamp even on error
    return false;
  }
};

// Run the check when this module is loaded
checkBackendAvailability();

// Also periodically recheck to see if backend has come back online
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'production') {
  setInterval(checkBackendAvailability, 30000); // Check every 30 seconds in production
}

// Track ongoing uploads
// const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api'; // Removed unused constant

class AssetService {
  async getAssets(
    params: AssetSearchParams = {}
  ): Promise<PaginatedResponse<Asset>> {
    try {
      // Create query parameters
      const queryParams = new URLSearchParams();
      if (params.search) queryParams.append('search', params.search);
      if (params.layer) queryParams.append('layer', params.layer);
      if (params.category) queryParams.append('category', params.category);
      if (params.subcategory)
        queryParams.append('subcategory', params.subcategory);
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      if (params.status) queryParams.append('status', params.status);

      // Convert date objects to ISO strings
      if (params.startDate) {
        const dateStr =
          params.startDate instanceof Date
            ? params.startDate.toISOString()
            : params.startDate.toString();
        queryParams.append('startDate', dateStr);
      }

      if (params.endDate) {
        const dateStr =
          params.endDate instanceof Date
            ? params.endDate.toISOString()
            : params.endDate.toString();
        queryParams.append('endDate', dateStr);
      }

      // Add tags if they exist
      if (params.tags && Array.isArray(params.tags)) {
        params.tags.forEach(tag => {
          queryParams.append('tags[]', tag);
        });
      }

      // Make the API request using the configured axios instance
      const response = await api.get(`/assets?${queryParams.toString()}`);

      // With axios, successful responses are in response.data
      // Axios will throw an error for failed requests, so we don't need to check response.ok
      const responseData = response.data;

      let assets: Asset[] = [];
      let pagination = {
        total: 0,
        page: params.page || 1,
        limit: params.limit || 10,
        pages: 1,
      };

      // Handle different response formats
      if (
        responseData.success &&
        responseData.assets &&
        Array.isArray(responseData.assets)
      ) {
        // Staging backend format: { success: true, assets: [], message: string }
        assets = responseData.assets.map((item: any) => {
          // Ensure each item has an id property (frontend uses id, backend uses _id)
          const normalizedAsset = { ...item };
          if (item._id && !item.id) {
            normalizedAsset.id = item._id;
          }
          // Apply URL normalization to fix environment mismatch CORS issues
          return normalizeApiResponse(normalizedAsset) as Asset;
        });

        pagination = {
          total: assets.length,
          page: params.page || 1,
          limit: params.limit || assets.length,
          pages: Math.ceil(assets.length / (params.limit || 10)),
        };
      } else if (responseData.success && responseData.data) {
        // New backend format: { success: true, data: { items: [], total: number, page: number, limit: number } }
        if (responseData.data.items && Array.isArray(responseData.data.items)) {
          assets = responseData.data.items.map((item: any) => {
            // Ensure each item has an id property (frontend uses id, backend uses _id)
            const normalizedAsset = { ...item };
            if (item._id && !item.id) {
              normalizedAsset.id = item._id;
            }
            // Apply URL normalization to fix environment mismatch CORS issues
            return normalizeApiResponse(normalizedAsset) as Asset;
          });

          pagination = {
            total: responseData.data.total || assets.length,
            page: responseData.data.page || 1,
            limit: responseData.data.limit || assets.length,
            pages:
              Math.ceil(
                (responseData.data.total || assets.length) /
                  (responseData.data.limit || 10)
              ) || 1,
          };
        } else if (Array.isArray(responseData.data)) {
          // Traditional format: response.data.data is an array of assets
          // Apply URL normalization to fix environment mismatch CORS issues
          assets = normalizeApiResponse(responseData.data) as Asset[];
          pagination = {
            total: assets.length,
            page: 1,
            limit: assets.length,
            pages: 1,
          };
        }
      } else if (Array.isArray(responseData)) {
        // Direct array response
        assets = responseData;
        pagination = {
          total: assets.length,
          page: 1,
          limit: assets.length,
          pages: 1,
        };
      } else {
        conditionalLog('Unexpected API response format:', responseData);
      }

      // Ensure all assets have an id property
      assets = assets.map(asset => {
        if (asset._id && !asset.id) {
          return { ...asset, id: asset._id };
        }
        return asset;
      });

      return {
        data: assets,
        pagination: pagination,
      };
    } catch (error) {
      console.error('Error fetching assets:', error);

      // Handle axios errors more specifically
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any;
        if (axiosError.response?.status === 401) {
          throw new Error('Authentication required');
        } else if (axiosError.response?.status === 500) {
          // Return empty result for 500 errors to trigger fallback
          return {
            data: [],
            pagination: {
              total: 0,
              page: 1,
              limit: 10,
              pages: 0,
            },
          };
        }
      }

      // For other errors, throw to be caught by calling code
      throw new Error(
        'Failed to fetch assets: ' +
          (error instanceof Error ? error.message : 'Unknown error')
      );
    }
  }

  /**
   * Get an asset by ID or Name
   * @param identifier Asset ID (MongoDB _id) or HFN (e.g., G.POP.TSW.001)
   * @returns Asset
   */
  async getAssetById(identifier: string): Promise<Asset> {
    try {
      const trimmedIdentifier = identifier.trim();
      if (!trimmedIdentifier || trimmedIdentifier === '') {
        throw new Error('Asset identifier is required and cannot be empty');
      }
      
      const response = await api.get<Asset>(`${getBackendUrl()}/assets/${encodeURIComponent(trimmedIdentifier)}`, {
        timeout: 8000,
      });
      if(response.status === 200) {
        return response.data;
      }

      throw new Error('Invalid response format');
    } catch (error) {
      console.error('Error fetching asset ' + identifier + ':', error);
      throw new Error(
        'Failed to fetch asset: ' +
          (error instanceof Error ? error.message : 'Unknown error')
      );
    }
  }

  /**
   * Get asset details (GET /api/v1/assets/{id}).
   * Returns AssetDetailsResponse with baseAsset; used by the detail page.
   */
  async getAssetDetails(id: string): Promise<AssetDetailsResponse> {
    const response = await api.get<
      AssetDetailsResponse | ApiResponse<AssetDetailsResponse>
    >(`${getBackendUrl()}/assets/${encodeURIComponent(id)}`, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const data = response.data as ApiResponse<AssetDetailsResponse>;
    const details = (data?.data ?? response.data) as AssetDetailsResponse;
    if (!details?.id) {
      throw new Error('Invalid asset details response');
    }
    return details;
  }

  /**
   * Get asset tags (GET /api/v1/assets/{id}/tags).
   */
  async getAssetTags(id: string): Promise<TagsResponse> {
    const response = await api.get<TagsResponse | ApiResponse<TagsResponse>>(
      `${getBackendUrl()}/assets/${encodeURIComponent(id)}/tags`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    const data = response.data as ApiResponse<TagsResponse>;
    const body = (data?.data ?? response.data) as TagsResponse;
    return body ?? { tags: [] };
  }

  /**
   * Get look metadata (GET /api/v1/assets/look/{assetId}/metadata).
   */
  async getLookMetadata(assetId: string): Promise<LookMetadataResponse> {
    const response = await api.get<
      LookMetadataResponse | ApiResponse<LookMetadataResponse>
    >(
      `${getBackendUrl()}/assets/look/${encodeURIComponent(assetId)}/metadata`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    const data = response.data as ApiResponse<LookMetadataResponse>;
    return (data?.data ?? response.data) as LookMetadataResponse;
  }

  /**
   * Get move metadata (GET /api/v1/assets/move/{assetId}/metadata).
   */
  async getMoveMetadata(assetId: string): Promise<MoveMetadataResponse> {
    const response = await api.get<
      MoveMetadataResponse | ApiResponse<MoveMetadataResponse>
    >(
      `${getBackendUrl()}/assets/move/${encodeURIComponent(assetId)}/metadata`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    const data = response.data as ApiResponse<MoveMetadataResponse>;
    return (data?.data ?? response.data) as MoveMetadataResponse;
  }

  /**
   * Get song metadata (GET /api/v1/assets/song/{assetId}/metadata).
   */
  async getSongMetadata(assetId: string): Promise<SongMetadataDetailsResponse> {
    const response = await api.get<
      SongMetadataDetailsResponse | ApiResponse<SongMetadataDetailsResponse>
    >(
      `${getBackendUrl()}/assets/song/${encodeURIComponent(assetId)}/metadata`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    const data = response.data as ApiResponse<SongMetadataDetailsResponse>;
    return (data?.data ?? response.data) as SongMetadataDetailsResponse;
  }

  /**
   * Get star metadata (GET /api/v1/assets/star/{assetId}/metadata).
   */
  async getStarMetadata(assetId: string): Promise<StarMetadataResponse> {
    const response = await api.get<
      StarMetadataResponse | ApiResponse<StarMetadataResponse>
    >(
      `${getBackendUrl()}/assets/star/${encodeURIComponent(assetId)}/metadata`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    const data = response.data as ApiResponse<StarMetadataResponse>;
    return (data?.data ?? response.data) as StarMetadataResponse;
  }

  /**
   * Get world metadata (GET /api/v1/assets/world/{assetId}/metadata).
   */
  async getWorldMetadata(assetId: string): Promise<WorldMetadataResponse> {
    const response = await api.get<
      WorldMetadataResponse | ApiResponse<WorldMetadataResponse>
    >(
      `${getBackendUrl()}/assets/world/${encodeURIComponent(assetId)}/metadata`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    const data = response.data as ApiResponse<WorldMetadataResponse>;
    return (data?.data ?? response.data) as WorldMetadataResponse;
  }

  /**
   * Get composite metadata (GET /assets/composite/{assetId}/metadata).
   */
  async getCompositeMetadata(assetId: string): Promise<CompositeMetadataResponse> {
    const response = await api.get<
      CompositeMetadataResponse | ApiResponse<CompositeMetadataResponse>
    >(
      `${getBackendUrl()}/assets/composite/${encodeURIComponent(
        assetId
      )}/metadata`
    );
    const data = response.data as ApiResponse<CompositeMetadataResponse>;
    return (data?.data ?? response.data) as CompositeMetadataResponse;
  }

  /**
   * Search variants by base asset ID (GET /assets/{layerPath}/search?baseAssetId=...&assetType=VARIANT&page&limit).
   * Layer path: G→song, S→star, L→look, M→move, W→world.
   */
  async getVariantsByBaseAssetId(
    layer: string,
    baseAssetId: string,
    page: number,
    limit: number
  ): Promise<{ results: any[]; totalCount: number }> {
    const pathByLayer: Record<string, string> = {
      G: 'song/search-registry',
      S: 'star/search',
      L: 'look/search',
      M: 'move/search',
      W: 'world/search',
    };
    const path = pathByLayer[layer];
    if (!path) {
      return { results: [], totalCount: 0 };
    }
    const url = new URL(`${getBackendUrl()}/assets/${path}`);
    url.searchParams.set('baseAssetId', baseAssetId);
    url.searchParams.set('assetType', 'VARIANT');
    url.searchParams.set('page', String(page));
    url.searchParams.set('limit', String(limit));
    const response = await api.get<{ results?: any[]; totalCount?: number; data?: { results?: any[]; totalCount?: number } }>(
      url.toString(),
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    const data = response.data;
    let results: any[] = data?.results ? data.results : [];
    let totalCount = data?.totalCount ? data.totalCount : results.length;
    return { results, totalCount };
  }

  /**
   * Upload a file
   * @param file File to upload
   * @param options Upload options
   * @returns File upload object
   */
  async uploadFile(
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<FileUpload> {
    const formData = new FormData();
    formData.append('file', file);

    const uploadId = Math.random().toString(36).substring(7);
    const fileUpload: FileUpload = {
      id: uploadId,
      file,
      progress: 0,
      status: 'pending',
      error: null,
      cancel: () => false,
    } as FileUpload;

    try {
      fileUpload.status = 'uploading';

      // Simulate upload progress
      let simulatedProgress = 0;
      const progressInterval = setInterval(() => {
        simulatedProgress += 10;
        if (simulatedProgress > 100) {
          simulatedProgress = 100;
          clearInterval(progressInterval);
        }
        fileUpload.progress = simulatedProgress;
        onProgress?.(simulatedProgress);
      }, 300);

      // This is for MOCK development - simulate a successful response after a delay
      // This prevents the 404 error when the API endpoint isn't available
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Mock response
      const mockResponse = {
        data: {
          filename: file.name,
          url: URL.createObjectURL(file), // Create a temporary URL for testing
          size: file.size,
          mimeType: file.type,
          originalName: file.name,
          success: true,
        },
      };

      clearInterval(progressInterval);
      fileUpload.progress = 100;
      onProgress?.(100);

      fileUpload.status = 'completed';
      (fileUpload as FileUpload).response = mockResponse.data;
      return fileUpload;
    } catch (error) {
      fileUpload.status = 'error';
      fileUpload.error =
        error instanceof Error ? error.message : 'Upload failed';
      throw error;
    }
  }

  /**
   * Create a new asset - FINAL TESTED SOLUTION
   *
   * This implementation has been systematically tested against the backend API
   * to ensure it matches exactly what the backend expects.
   *
   * @param assetData The asset data to use for creation
   * @returns The created asset or a mock asset if in mock mode
   */
  async createAsset(assetData: AssetCreateRequest): Promise<Asset> {
    // CRITICAL DEBUG: Log exactly what we're about to send to backend
    conditionalLog(
      '%c🚨 [ASSET CREATION] createAsset called!',
      'background: #ff6b6b; color: white; font-size: 16px; padding: 8px;'
    );
    conditionalLog('📦 [ASSET DATA] Complete data received:', assetData);

    // 🛡️ CONTRACT ENFORCEMENT: Validate and auto-fix data before backend submission
    const enforcedAssetData = ContractEnforcer.enforceAssetContract(assetData);
    conditionalLog(
      '🛡️ [CONTRACT ENFORCED] Data after enforcement:',
      enforcedAssetData
    );

    // Check for Stars layer specific data
    if (enforcedAssetData.layer === 'S') {
      conditionalLog(
        '%c⭐ [STARS LAYER] Star-specific metadata:',
        'background: #4ecdc4; color: white; font-size: 14px; padding: 6px;'
      );
      conditionalLog('🌟 starMetadata:', (enforcedAssetData as any).starMetadata);
      conditionalLog(
        '🌟 aiMetadata.starsMetadata:',
        (enforcedAssetData as any).aiMetadata?.starsMetadata
      );
      conditionalLog(
        '🎯 baseStarId from starMetadata:',
        (assetData as any).starMetadata?.baseStarId
      );
      conditionalLog(
        '🎯 variantType:',
        (assetData as any).starMetadata?.variantType
      );
    }

    try {
      // Real API implementation only (no mock fallback)

      // Create FormData object with the EXACT fields expected by backend
      const formData = new FormData();

      // Add file (required by backend)
      if (enforcedAssetData.files && enforcedAssetData.files.length > 0) {
        const file = enforcedAssetData.files[0];
        formData.append('file', file);
      } else {
        console.warn('No file provided for asset creation');
        throw new Error('File is required for asset creation');
      }

      // === CRITICAL: Add all fields EXACTLY as expected by backend ===
      // These field names and formats are confirmed to work with the backend
      // DO NOT include 'name' field - backend will reject it

      // CRITICAL FIX: Ensure layer is always sent as string (C, G, S, etc.) not numeric (1, 9, 2)
      const layerToSend = String(enforcedAssetData.layer).toUpperCase();
      conditionalLog(
        `🔍 [ASSET SERVICE] Layer being sent to backend: "${layerToSend}" (original: "${
          enforcedAssetData.layer
        }", type: ${typeof enforcedAssetData.layer})`
      );
      formData.append('layer', layerToSend);

      // V1.5.0 MIGRATION: Convert HFN to MFA for backend communication
      // This ensures backend receives standardized MFA codes while users see HFN names
      const originalCategory = enforcedAssetData.category || 'Pop';
      const originalSubcategory = enforcedAssetData.subcategory || 'Global';

      // Convert HFN to MFA using V1.5.0 mapping
      const mfaCategory = convertHFNToMFA_V150(originalCategory);
      const mfaSubcategory = convertHFNToMFA_V150(originalSubcategory);

      conditionalLog(`🔄 [ASSET SERVICE V1.5.0] MFA Conversion:`, {
        category: `${originalCategory} → ${mfaCategory}`,
        subcategory: `${originalSubcategory} → ${mfaSubcategory}`,
      });

      // Use MFA codes for backend communication (standardization)
      const categoryCode = mfaCategory;
      const subcategoryCode = mfaSubcategory;

      // Send the 3-letter alphabetic codes directly to the backend
      // TEST: Try both formats to determine what backend expects
      conditionalLog(
        `🔍 [ASSET SERVICE] Testing subcategory formats - Original: ${subcategoryCode}, Full: ${categoryCode}.${subcategoryCode}`
      );

      // Try original format first (just the subcategory part)
      formData.append('category', categoryCode);
      formData.append('subcategory', subcategoryCode);
      formData.append('source', enforcedAssetData.source || 'ReViz');

      // IMPORTANT: Backend validation requires the description field to be non-empty
      // If description is empty, use a default value based on the asset name
      const descriptionToSend =
        enforcedAssetData.description ||
        `Asset ${enforcedAssetData.name} (${enforcedAssetData.layer}.${enforcedAssetData.category}.${enforcedAssetData.subcategory})`;
      formData.append('description', descriptionToSend);

      // Tags must be a JSON string per backend expectations
      if (enforcedAssetData.tags && enforcedAssetData.tags.length > 0) {
        formData.append('tags', JSON.stringify(enforcedAssetData.tags));
      } else {
        formData.append('tags', JSON.stringify(['general']));
      }

      // Required nested objects
      formData.append(
        'trainingData',
        JSON.stringify({
          prompts: [],
          images: [],
          videos: [],
        })
      );

      formData.append(
        'rights',
        JSON.stringify({
          source: 'Original',
          rights_split: '100%',
        })
      );

      // Components - using array bracket format
      // The backend expects this specific format
      // Check if components exist in metadata or root level (for composite assets)
      const components =
        (enforcedAssetData as any).components ||
        (enforcedAssetData as any).metadata?.components;
      if (components && Array.isArray(components) && components.length > 0) {
        conditionalLog(
          `🔍 [COMPONENT DEBUG] Processing ${components.length} components:`,
          components
        );

        // Add each component as a separate form field
        components.forEach((component: any, index: number) => {
          conditionalLog(`🔍 [COMPONENT DEBUG] Component ${index}:`, component);

          // CRITICAL FIX: Use HFN (friendlyName/name) instead of partial MFA (nna_address)
          // The backend expects full HFN codes like "S.POP.BAS.001", not partial MFA like "S.001.001"
          let componentId;

          if (component.friendlyName && component.friendlyName.includes('.')) {
            // Use friendlyName if it looks like a valid HFN (contains dots)
            componentId = component.friendlyName;
            conditionalLog(
              `🔍 [COMPONENT DEBUG] Using friendlyName: ${componentId}`
            );
          } else if (component.name && component.name.includes('.')) {
            // Use name if it looks like a valid HFN (contains dots)
            componentId = component.name;
            conditionalLog(`🔍 [COMPONENT DEBUG] Using name: ${componentId}`);
          } else {
            // Fallback to component ID or object itself
            componentId = component.id || component;
            conditionalLog(
              `🔍 [COMPONENT DEBUG] Using fallback ID: ${componentId}`
            );
          }

          conditionalLog(
            `🔍 [COMPONENT DEBUG] Final componentId for backend: ${componentId}`
          );
          formData.append('components[]', componentId);
        });
      }
      // BACKEND FIX: Omit components field entirely when empty - backend defaults to []
      // else { formData.append('components[]', ''); } // Removed - backend team recommendation

      // === PHASE 2B FIELDS (NEW) ===
      conditionalLog(
        '%c=== FORMDATA PHASE 2B DEBUG ===',
        'background: #e91e63; color: white;'
      );
      conditionalLog(
        '🔍 assetData.creatorDescription:',
        enforcedAssetData.creatorDescription
      );
      conditionalLog(
        '🔍 typeof creatorDescription:',
        typeof enforcedAssetData.creatorDescription
      );
      conditionalLog('🔍 truthy check:', !!enforcedAssetData.creatorDescription);

      if (enforcedAssetData.creatorDescription) {
        formData.append(
          'creatorDescription',
          enforcedAssetData.creatorDescription
        );
        conditionalLog(
          '✅ Added creatorDescription to FormData:',
          enforcedAssetData.creatorDescription
        );
      } else {
        conditionalLog('❌ creatorDescription is falsy, not added to FormData');
      }

      if (enforcedAssetData.albumArt) {
        formData.append('albumArt', enforcedAssetData.albumArt);
        conditionalLog(
          '✅ Added albumArt to FormData:',
          enforcedAssetData.albumArt
        );
      } else {
        conditionalLog('⚠️ albumArt is falsy, not added to FormData');
      }

      // CRITICAL FIX: Do not send full aiMetadata to prevent MongoDB duplication
      // Layer-specific metadata (songMetadata, starMetadata, etc.) already contains the same data
      if (enforcedAssetData.aiMetadata) {
        conditionalLog('🚨 [DUPLICATION FIX] Skipping full aiMetadata to prevent 3x duplication in MongoDB');
        conditionalLog('📋 [DUPLICATION FIX] Layer-specific metadata will be used instead');

        // Only send specific AI-generated context fields for composite assets
        if (enforcedAssetData.aiMetadata.songContext) {
          formData.append('songContext', enforcedAssetData.aiMetadata.songContext);
          conditionalLog('✅ Added songContext (for composite assets)');
        }
        if (enforcedAssetData.aiMetadata.remixContext) {
          formData.append('remixContext', enforcedAssetData.aiMetadata.remixContext);
          conditionalLog('✅ Added remixContext (for composite assets)');
        }
      } else {
        conditionalLog('⚠️ aiMetadata is falsy, not added to FormData');
      }

      // Phase 2C: Add songMetadata to FormData (CRITICAL FIX)
      conditionalLog(
        '🔍 songMetadata debug - Value:',
        enforcedAssetData.songMetadata
      );
      conditionalLog(
        '🔍 songMetadata debug - Type:',
        typeof enforcedAssetData.songMetadata
      );
      conditionalLog(
        '🔍 songMetadata debug - Truthy:',
        !!enforcedAssetData.songMetadata
      );

      if (enforcedAssetData.songMetadata) {
        // Convert string values to numeric values for backend compatibility
        const convertedSongMetadata = {
          ...enforcedAssetData.songMetadata,
        } as any;

        // BACKEND EXPECTS STRINGS, NOT NUMBERS!
        // Helper function to ensure High/Medium/Low values remain as strings
        const ensureStringFormat = (value: any): string => {
          if (typeof value === 'string') return value;
          if (typeof value === 'number') {
            // Convert numbers back to strings if needed
            switch (value) {
              case 1:
                return 'High';
              case 2:
                return 'Medium';
              case 3:
                return 'Low';
              default:
                return 'Medium'; // Default fallback
            }
          }
          return 'Medium'; // Default fallback
        };

        // Ensure all fields remain as strings for backend compatibility
        if (convertedSongMetadata.danceability) {
          convertedSongMetadata.danceability = ensureStringFormat(
            convertedSongMetadata.danceability
          );
        }

        if (convertedSongMetadata.energy) {
          convertedSongMetadata.energy = ensureStringFormat(
            convertedSongMetadata.energy
          );
        }

        if (convertedSongMetadata.valence) {
          convertedSongMetadata.valence = ensureStringFormat(
            convertedSongMetadata.valence
          );
        }

        if (convertedSongMetadata.acousticness) {
          convertedSongMetadata.acousticness = ensureStringFormat(
            convertedSongMetadata.acousticness
          );
        }

        if (convertedSongMetadata.instrumentalness) {
          convertedSongMetadata.instrumentalness = ensureStringFormat(
            convertedSongMetadata.instrumentalness
          );
        }

        if (convertedSongMetadata.liveness) {
          convertedSongMetadata.liveness = ensureStringFormat(
            convertedSongMetadata.liveness
          );
        }

        if (convertedSongMetadata.speechiness) {
          convertedSongMetadata.speechiness = ensureStringFormat(
            convertedSongMetadata.speechiness
          );
        }

        // Popularity should remain numeric if it's a number, string if string
        if (
          convertedSongMetadata.popularity &&
          typeof convertedSongMetadata.popularity !== 'number'
        ) {
          // Only convert non-numeric popularity to string format
          convertedSongMetadata.popularity = ensureStringFormat(
            convertedSongMetadata.popularity
          );
        }

        // CRITICAL FIX: Backend expects language as ARRAY not string
        // Based on backend analysis: language field should be string[] format
        if (typeof convertedSongMetadata.language === 'string') {
          convertedSongMetadata.language = [convertedSongMetadata.language];
          conditionalLog(
            '🌍 [LANGUAGE FIX] Converted string to array:',
            convertedSongMetadata.language
          );
        } else if (
          !Array.isArray(convertedSongMetadata.language) ||
          convertedSongMetadata.language === null ||
          convertedSongMetadata.language === undefined
        ) {
          convertedSongMetadata.language = ['English'];
          conditionalLog('🌍 [LANGUAGE FIX] Set default array: [English]');
        }

        // ADDITIONAL SAFETY: Ensure language is always an array before sending to backend
        if (!Array.isArray(convertedSongMetadata.language)) {
          convertedSongMetadata.language = ['English'];
          conditionalLog(
            '🛡️ [LANGUAGE SAFETY] Final language safety check: set to [English]'
          );
        }

        // ULTIMATE SAFETY: Force language to be an array no matter what
        if (!Array.isArray(convertedSongMetadata.language)) {
          conditionalLog(
            '🚨 [LANGUAGE CRITICAL] Language is not an array! Type:',
            typeof convertedSongMetadata.language,
            'Value:',
            convertedSongMetadata.language
          );
          convertedSongMetadata.language = [
            String(convertedSongMetadata.language || 'English'),
          ];
          conditionalLog(
            '🔧 [LANGUAGE CRITICAL] Forced conversion to array:',
            convertedSongMetadata.language
          );
        }

        conditionalLog(
          '🔄 [BACKEND COMPATIBILITY] Ensuring songMetadata values are in correct format for backend'
        );
        conditionalLog(
          '🔄 Original danceability:',
          (enforcedAssetData.songMetadata as any).danceability,
          '-> Converted:',
          (convertedSongMetadata as any).danceability
        );
        conditionalLog(
          '🔄 Original energy:',
          (enforcedAssetData.songMetadata as any).energy,
          '-> Converted:',
          (convertedSongMetadata as any).energy
        );

        formData.append('songMetadata', JSON.stringify(convertedSongMetadata));
        conditionalLog(
          '✅ Added songMetadata to FormData:',
          JSON.stringify(convertedSongMetadata)
        );
      } else {
        conditionalLog('❌ songMetadata is falsy, not added to FormData');
      }

      // Phase 2D: Enhanced metadata for all layers
      // CRITICAL: Check if starMetadata exists and add it separately for Stars layer variants
      // Check both aiMetadata.starsMetadata and root-level starMetadata
      const starsMetadataFromAI = enforcedAssetData.aiMetadata?.starsMetadata;
      const starsMetadataFromRoot = (enforcedAssetData as any).starMetadata;
      const starsMetadata = starsMetadataFromAI || starsMetadataFromRoot;

      conditionalLog(
        '%c🔍 [STARS DEBUG] Checking starMetadata condition',
        'background: #purple; color: white;'
      );
      conditionalLog('🔍 enforcedAssetData.layer:', enforcedAssetData.layer);
      conditionalLog('🔍 starsMetadataFromAI:', starsMetadataFromAI);
      conditionalLog('🔍 starsMetadataFromRoot:', starsMetadataFromRoot);
      conditionalLog('🔍 final starsMetadata:', starsMetadata);

      if (enforcedAssetData.layer === 'S' && starsMetadata) {
        conditionalLog(
          '%c⭐ [STARS CRITICAL] Adding starMetadata separately for variant support',
          'background: #ff1744; color: white; font-size: 14px; padding: 6px;'
        );

        // 🎯 VOCABULARY NORMALIZATION: Apply hair color mappings for Stars layer
        // All assets are base assets - remove variant fields
        const normalizedStarsMetadata = {
          ...starsMetadata,
        };
        // Explicitly remove variant fields
        delete normalizedStarsMetadata.variantName;
        delete normalizedStarsMetadata.variantType;
        delete normalizedStarsMetadata.variantDescription;

        // Apply hair color vocabulary normalization
        if (normalizedStarsMetadata.hairColor) {
          const originalHairColor = normalizedStarsMetadata.hairColor;
          normalizedStarsMetadata.hairColor = normalizeHairColor(
            normalizedStarsMetadata.hairColor
          );
          if (originalHairColor !== normalizedStarsMetadata.hairColor) {
            conditionalLog(
              `🎨 [VOCABULARY STARS] Hair color normalized: "${originalHairColor}" → "${normalizedStarsMetadata.hairColor}"`
            );
          }
        }

        // Handle nested physicalTraits.hairColor
        if (normalizedStarsMetadata.physicalTraits?.hairColor) {
          const originalPhysicalHairColor =
            normalizedStarsMetadata.physicalTraits.hairColor;
          normalizedStarsMetadata.physicalTraits.hairColor = normalizeHairColor(
            normalizedStarsMetadata.physicalTraits.hairColor
          );
          if (
            originalPhysicalHairColor !==
            normalizedStarsMetadata.physicalTraits.hairColor
          ) {
            conditionalLog(
              `🎨 [VOCABULARY STARS] PhysicalTraits hair color normalized: "${originalPhysicalHairColor}" → "${normalizedStarsMetadata.physicalTraits.hairColor}"`
            );
          }
        }

        formData.append(
          'starMetadata',
          JSON.stringify(normalizedStarsMetadata)
        );
        conditionalLog(
          '✅ Added starMetadata to FormData (Stars-specific with vocabulary):',
          JSON.stringify(normalizedStarsMetadata)
        );
        conditionalLog(
          '🎯 [STARS] hairStyle being sent:',
          starsMetadata.hairStyle
        );
        
        // All assets are base assets - never send baseAssetHumanFriendlyName
      } else {
        conditionalLog(
          '%c❌ [STARS DEBUG] Condition failed - starMetadata will NOT be added',
          'background: #red; color: white;'
        );
        if (enforcedAssetData.layer !== 'S') {
          conditionalLog('❌ Layer is not S, it is:', enforcedAssetData.layer);
        }
        if (!starsMetadata) {
          conditionalLog(
            '❌ starsMetadata is falsy - AI:',
            starsMetadataFromAI,
            'Root:',
            starsMetadataFromRoot
          );
        }
      }

      if (enforcedAssetData.lookMetadata || enforcedAssetData.looksMetadata) {
        // 🚨 CRITICAL FIX: Ensure jewelryType is always a string to prevent backend validation error
        const lookMetadata = enforcedAssetData.lookMetadata || enforcedAssetData.looksMetadata || {};
        const sanitizedLookMetadata = { ...lookMetadata };
        
        // All assets are base assets - remove variant fields
        delete sanitizedLookMetadata.variantName;
        delete sanitizedLookMetadata.variantType;
        delete sanitizedLookMetadata.variantDescription;
        
        if (sanitizedLookMetadata.jewelryType) {
          if (Array.isArray(sanitizedLookMetadata.jewelryType)) {
            // Convert array to string (take first element or 'None' if empty)
            sanitizedLookMetadata.jewelryType =
              sanitizedLookMetadata.jewelryType.length > 0
                ? sanitizedLookMetadata.jewelryType[0]
                : 'None';
            conditionalLog(
              '🔧 [JEWELRY TYPE FIX] Converted array to string:',
              sanitizedLookMetadata.jewelryType
            );
          } else if (typeof sanitizedLookMetadata.jewelryType !== 'string') {
            // Ensure it's always a string
            sanitizedLookMetadata.jewelryType = String(
              sanitizedLookMetadata.jewelryType
            );
            conditionalLog(
              '🔧 [JEWELRY TYPE FIX] Converted to string:',
              sanitizedLookMetadata.jewelryType
            );
          }
        }
        formData.append('lookMetadata', JSON.stringify(sanitizedLookMetadata));
        conditionalLog(
          '✅ Added lookMetadata to FormData:',
          JSON.stringify(sanitizedLookMetadata)
        );
      }

      if (enforcedAssetData.moveMetadata || enforcedAssetData.movesMetadata) {
        const moveMetadata = enforcedAssetData.moveMetadata || enforcedAssetData.movesMetadata || {};
        const sanitizedMoveMetadata = { ...moveMetadata };
        
        // All assets are base assets - remove variant fields
        delete sanitizedMoveMetadata.variantName;
        delete sanitizedMoveMetadata.variantType;
        delete sanitizedMoveMetadata.variantDescription;
        
        formData.append(
          'moveMetadata',
          JSON.stringify(sanitizedMoveMetadata)
        );
        conditionalLog(
          '✅ Added moveMetadata to FormData:',
          JSON.stringify(sanitizedMoveMetadata)
        );
      }

      if (enforcedAssetData.worldMetadata || enforcedAssetData.worldsMetadata) {
        const worldMetadata = enforcedAssetData.worldMetadata || enforcedAssetData.worldsMetadata || {};
        const sanitizedWorldMetadata = { ...worldMetadata };
        
        // All assets are base assets - remove variant fields
        delete sanitizedWorldMetadata.variantName;
        delete sanitizedWorldMetadata.variantType;
        delete sanitizedWorldMetadata.variantDescription;
        
        formData.append(
          'worldMetadata',
          JSON.stringify(sanitizedWorldMetadata)
        );
        conditionalLog(
          '✅ Added worldMetadata to FormData:',
          JSON.stringify(sanitizedWorldMetadata)
        );
      }

      // Debug: Log FormData entries (TypeScript compatible approach)
      conditionalLog('🔍 FormData Debug - Key entries logged:');
      const formDataKeys = [
        'layer',
        'category',
        'subcategory',
        'description',
        'tags',
        'creatorDescription',
        'albumArt',
        'songMetadata',
        'starMetadata',
        'lookMetadata',
        'moveMetadata',
        'worldMetadata',
        'aiMetadata',
        'file',
      ];
      formDataKeys.forEach(key => {
        const value = formData.get(key);
        if (value) {
          if (value instanceof File) {
            conditionalLog(`  ${key}: [File] ${value.name} (${value.size} bytes)`);
          } else {
            const stringValue = value.toString();
            conditionalLog(
              `  ${key}: ${
                stringValue.length > 100
                  ? stringValue.substring(0, 100) + '...'
                  : stringValue
              }`
            );
          }
        }
      });
      conditionalLog(
        '🔍 FormData total estimated size:',
        JSON.stringify(enforcedAssetData).length +
          (enforcedAssetData.files?.[0]?.size || 0),
        'bytes'
      );

      // Make the API request using fetch for better FormData handling
      // SMART ROUTING: Use proxy for small files, direct backend for large files
      // Environment-aware routing for staging vs production backends
      const fileSize =
        enforcedAssetData.files?.length > 0
          ? enforcedAssetData.files[0].size
          : 0;
      
      // Layer-specific endpoint routing: Stars, Moves, Looks, Worlds use their specific endpoints
      let uploadUrl: string;
      let useDirect: boolean;
      
      if (enforcedAssetData.layer === 'S') {
        // Stars layer: route to /assets/star endpoint
        const backendUrl = getBackendUrl();
        uploadUrl = `${backendUrl}/assets/star`;
        useDirect = true;
        conditionalLog('⭐ [STAR ROUTING] Routing to /assets/star endpoint');
      } else if (enforcedAssetData.layer === 'M') {
        // Moves layer: route to /assets/move endpoint
        const backendUrl = getBackendUrl();
        uploadUrl = `${backendUrl}/assets/move`;
        useDirect = true;
        conditionalLog('💃 [MOVE ROUTING] Routing to /assets/move endpoint');
      } else if (enforcedAssetData.layer === 'L') {
        // Looks layer: route to /assets/look endpoint
        const backendUrl = getBackendUrl();
        uploadUrl = `${backendUrl}/assets/look`;
        useDirect = true;
        conditionalLog('👗 [LOOK ROUTING] Routing to /assets/look endpoint');
      } else if (enforcedAssetData.layer === 'W') {
        // Worlds layer: route to /assets/world endpoint
        const backendUrl = getBackendUrl();
        uploadUrl = `${backendUrl}/assets/world`;
        useDirect = true;
        conditionalLog('🌍 [WORLD ROUTING] Routing to /assets/world endpoint');
      } else {
        // Other layers: use existing endpoint logic
        const uploadConfig = getUploadEndpoint(fileSize);
        uploadUrl = uploadConfig.url;
        useDirect = uploadConfig.useDirect;
      }

      // Log environment info on first upload (debug mode only) - PERFORMANCE: Reduced logging
      const config = getEnvironmentConfig();
      if (config.enableDebugLogging) {
        conditionalLog(
          `📤 Uploading via ${
            useDirect ? 'DIRECT' : 'PROXY'
          } (${(fileSize / 1024 / 1024).toFixed(1)}MB) to ${uploadUrl}`
        );

        conditionalLog('🔐 Auth: injected by axios interceptor');
      }

      const response = await api.post(uploadUrl, formData, {
        timeout: 30000,
      });
      try {
        const responseData = response.data;
        // Apply URL normalization to fix environment mismatch CORS issues
        return normalizeApiResponse(responseData.data ?? responseData);
      } catch (e) {
        console.error('Failed to parse successful response', e);
        return this.mockCreateAsset(enforcedAssetData);
      }
    } catch (error) {
      console.error('Error in asset creation:', error);
      // Don't return mock data on real API errors - throw the error to caller
      throw error;
    }
  }

  /**
   * Mock implementation to create an asset (fallback for errors)
   * @param assetData Asset data to create mock from
   * @param apiAssetData Optional processed API data
   * @returns Mocked asset with all required fields
   */
  private mockCreateAsset(
    assetData: AssetCreateRequest,
    apiAssetData?: any
  ): Asset {
    // Extract metadata from the custom assetData structure
    const customMetadata = (assetData as any).metadata || {};

    // Map uploaded files to AssetFile format safely
    const uploadedFiles: AssetFile[] = (customMetadata.uploadedFiles || []).map(
      (file: FileUploadResponse) => ({
        id: `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        filename: file.filename,
        contentType: file.mimeType,
        size: file.size,
        url: file.url,
        uploadedAt: new Date().toISOString(),
        thumbnailUrl: file.mimeType.startsWith('image/') ? file.url : undefined,
      })
    );

    // Extract metadata properly for consistent HFN/MFA values
    const hfn =
      customMetadata.hfn || customMetadata.humanFriendlyName || assetData.name;

    // For MFA, use the value provided in metadata in the proper priority order
    const mfa =
      assetData.nnaAddress || // First check if it's at the root level
      customMetadata.machineFriendlyAddress || // Then check property variations
      customMetadata.mfa;

    if (!mfa) {
      console.warn(
        'No MFA found in asset data! This indicates a potential issue with taxonomy selection.'
      );
    }

    const layerName = customMetadata.layerName || 'Unknown Layer';

    // Generate a mock response
    const mockAsset: Asset = {
      id: `mock-asset-${Date.now()}`,
      name: assetData.name,
      friendlyName: assetData.name,
      machineFriendlyName: mfa,
      type: 'standard',
      imageUrl: 'https://storage.googleapis.com/mock-bucket/',
      description: assetData.description || '',
      layer: assetData.layer,
      categoryCode: (assetData as any).categoryCode || '',
      subcategoryCode: (assetData as any).subcategoryCode || '',
      category: assetData.category,
      subcategory: assetData.subcategory || 'DIV',
      tags: assetData.tags || [],
      files: uploadedFiles,
      metadata: {
        ...customMetadata,
        humanFriendlyName: hfn, // Always set these consistently
        machineFriendlyAddress: mfa,
        hfn: hfn, // Include duplicate keys for better compatibility
        mfa: mfa, // Include duplicate keys for better compatibility
        layerName: layerName, // Include layer name in metadata
        fileUrl: 'https://storage.googleapis.com/mock-bucket/',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      status: 'active',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: 'user@example.com',
    };

    // Register in our asset registry for duplicate detection
    if (assetData.files && assetData.files.length > 0) {
      const file = assetData.files[0];
      if (file) {
        const fingerprint = {
          name: file.name,
          size: file.size,
          type: file.type,
          lastModified: file.lastModified,
          hash: `${file.name}-${file.size}-${file.lastModified}`, // Simple hash
        };

        assetRegistryService.registerAsset(mockAsset, fingerprint);
      }
    }

    return mockAsset;
  }

  /**
   * Update asset metadata via layer-specific PUT (song, star, look, move, world only).
   * PUT /api/v1/assets/{song|star|look|move|world}/{assetId} with *AssetUpdateRequest body.
   * @param layer Layer code G, S, L, M, W
   * @param assetId Asset UUID
   * @param body Description, tags, metadata (no category/subcategory)
   * @returns Updated metadata response (same shape as GET metadata)
   */
  async updateAssetMetadata(
    layer: string,
    assetId: string,
    body:
      | LookAssetUpdateRequest
      | MoveAssetUpdateRequest
      | SongAssetUpdateRequest
      | StarAssetUpdateRequest
      | WorldAssetUpdateRequest
  ): Promise<
    | LookMetadataResponse
    | MoveMetadataResponse
    | SongMetadataDetailsResponse
    | StarMetadataResponse
    | WorldMetadataResponse
  > {
    const path = LAYER_TO_PATH[layer as AssetLayer];
    if (!path) {
      throw new Error(`Unsupported layer for metadata update: ${layer}`);
    }
    try {
      const response = await api.put<
        | LookMetadataResponse
        | MoveMetadataResponse
        | SongMetadataDetailsResponse
        | StarMetadataResponse
        | WorldMetadataResponse
        | ApiResponse<unknown>
      >(`${getBackendUrl()}/assets/${path}/${encodeURIComponent(assetId)}`, body,
      {
      });
      const data = response.data as ApiResponse<unknown>;
      const payload = data?.data ?? response.data;
      return payload as
        | LookMetadataResponse
        | MoveMetadataResponse
        | SongMetadataDetailsResponse
        | StarMetadataResponse
        | WorldMetadataResponse;
    } catch (error: any) {
      if (error.response) {
        const status = error.response.status;
        const msg =
          error.response.data?.message ||
          error.response.data?.error ||
          error.response.statusText ||
          'Unknown error';
        if (status === 400) throw new Error(`Invalid request: ${msg}`);
        if (status === 401) throw new Error('Unauthorized');
        if (status === 404) throw new Error('Asset not found');
        throw new Error(`Backend error (${status}): ${msg}`);
      }
      if (error.request) throw new Error('Network error: No response from backend');
      throw new Error(error.message || 'Failed to update asset metadata');
    }
  }

  /**
   * Update an asset
   * @param id Asset ID
   * @param updateData Update data
   * @returns Updated asset
   */
  async updateAsset(
    id: string,
    updateData: AssetUpdateRequest
  ): Promise<Asset> {
    // Wrap the update operation in retry logic for concurrency errors
    return retryWithBackoff(async () => {
      try {
        // V1.5.0 MIGRATION: Convert HFN to MFA if category/subcategory are being updated
        const processedUpdateData = { ...updateData };

        if (updateData.category) {
          const mfaCategory = convertHFNToMFA_V150(updateData.category);
          processedUpdateData.category = mfaCategory;
          conditionalLog(
            `🔄 [ASSET SERVICE V1.5.0] Update category conversion: ${updateData.category} → ${mfaCategory}`
          );
        }

        if (updateData.subcategory) {
          const mfaSubcategory = convertHFNToMFA_V150(updateData.subcategory);
          processedUpdateData.subcategory = mfaSubcategory;
          conditionalLog(
            `🔄 [ASSET SERVICE V1.5.0] Update subcategory conversion: ${updateData.subcategory} → ${mfaSubcategory}`
          );
        }

        // Debug: Log the exact payload being sent to backend
        conditionalLog('🚀 [UPDATE PAYLOAD] Sending to backend:', JSON.stringify(processedUpdateData, null, 2));

        // Use same proxy route as GET requests - this is the key fix!
        const response = await api.put<ApiResponse<Asset>>(
          `/assets/${id}`,
          processedUpdateData
        );
        // Apply URL normalization to fix environment mismatch CORS issues
        const normalizedAsset = normalizeApiResponse(response.data.data) as Asset;
        return normalizedAsset;
      } catch (error: any) {
        console.error(`Error updating asset ${id}:`, error);

        // Enhanced error logging to capture backend response details
        if (error.response) {
          console.error('🚨 [ASSET UPDATE ERROR] Backend response details:', {
            status: error.response.status,
            statusText: error.response.statusText,
            data: error.response.data,
            headers: error.response.headers
          });
          // User requested: Enhanced JSON logging for detailed backend validation errors
          console.error('[UPDATE ERROR]', error.response?.status, JSON.stringify(error.response?.data, null, 2));

          // Try to extract meaningful error message from backend
          const backendMessage = error.response.data?.message ||
                                error.response.data?.error ||
                                error.response.statusText ||
                                'Unknown backend error';

          throw new Error(`Backend error (${error.response.status}): ${backendMessage}`);
        } else if (error.request) {
          console.error('🚨 [ASSET UPDATE ERROR] Request failed - no response from backend:', error.request);
          throw new Error('Network error: No response from backend');
        } else {
          console.error('🚨 [ASSET UPDATE ERROR] Request setup error:', error.message);
          throw new Error(`Request error: ${error.message}`);
        }
      }
    }, 3, 500, `Asset ${id} update`);
  }

  /**
   * Delete an asset
   * @param id Asset ID
   */
  async deleteAsset(id: string): Promise<void> {
    try {
      await api.delete<ApiResponse<void>>(`${getBackendUrl()}/assets/${id}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } catch (error: any) {
      console.error(`Error deleting asset ${id}:`, error);
      if (error.response) {
        const status = error.response.status;
        const backendMessage =
          error.response.data?.message ||
          error.response.data?.error ||
          error.response.statusText ||
          (status === 409 ? 'Asset is in use and cannot be deleted.' : 'Unknown backend error');
        throw new Error(backendMessage);
      }
      if (error.request) {
        throw new Error('Network error: No response from backend');
      }
      throw new Error(error.message || 'Failed to delete asset');
    }
  }

  /**
   * Replace an asset's file with a new file
   * @param assetIdOrName Asset MongoDB ID or HFN
   * @param formData FormData containing file, versionNotes, and optional updateDto
   * @returns Updated asset with new file
   */
  async replaceAssetFile(
    assetIdOrName: string,
    formData: FormData
  ): Promise<ApiResponse<{
    asset: Asset;
    previousFileUrl: string;
    newFileUrl: string;
    version: number;
  }>> {
    try {
      // Determine routing based on file size (if available)
      const file = formData.get('file') as File;
      const fileSize = file?.size || 0;
      const SMALL_FILE_THRESHOLD = 4 * 1024 * 1024; // 4MB

      // Use smart routing like registration
      const endpoint =
        fileSize > SMALL_FILE_THRESHOLD
          ? `${getBackendUrl()}/assets/${assetIdOrName}/replace-file`
          : `/assets/${assetIdOrName}/replace-file`;

      console.log(
        `📤 [REPLACE FILE] Uploading ${(fileSize / 1024 / 1024).toFixed(2)}MB file via ${
          fileSize > SMALL_FILE_THRESHOLD ? 'DIRECT backend' : 'PROXY'
        }`
      );

      const response = await api.put<ApiResponse<{
        asset: Asset;
        previousFileUrl: string;
        newFileUrl: string;
        version: number;
      }>>(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('✅ [REPLACE FILE] File replaced successfully');
      return response.data;
    } catch (error) {
      console.error('❌ [REPLACE FILE] Error:', error);
      throw error;
    }
  }

  /**
   * Replace asset file via layer-specific PUT (song, star, look, move, world only).
   * PUT /api/v1/assets/{song|star|look|move|world}/{assetId}/file with multipart assetFile.
   * @param layer Layer code G, S, L, M, W
   * @param assetId Asset UUID
   * @param file New file (audio for song; image for star/look; video for move/world)
   * @returns StoredAssetDTO (id, humanFriendlyName, machineFriendlyAddress)
   */
  async replaceAssetFileByLayer(
    layer: string,
    assetId: string,
    file: File
  ): Promise<StoredAssetDTO> {
    const path = LAYER_TO_PATH[layer as AssetLayer];
    if (!path) {
      throw new Error(`Unsupported layer for file replace: ${layer}`);
    }
    const formData = new FormData();
    formData.append('assetFile', file);
    try {
      const response = await api.put<StoredAssetDTO | ApiResponse<StoredAssetDTO>>(
        `${getBackendUrl()}/assets/${path}/${encodeURIComponent(assetId)}/file`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      const data = response.data as ApiResponse<StoredAssetDTO>;
      const payload = data?.data ?? response.data;
      return payload as StoredAssetDTO;
    } catch (error: any) {
      if (error.response) {
        const status = error.response.status;
        const msg =
          error.response.data?.message ||
          error.response.data?.error ||
          error.response.statusText ||
          'Unknown error';
        if (status === 400) throw new Error(`Invalid file type or request: ${msg}`);
        if (status === 401) throw new Error('Unauthorized');
        if (status === 404) throw new Error('Asset not found');
        if (status === 503) throw new Error('Upload or delete failed');
        throw new Error(`Backend error (${status}): ${msg}`);
      }
      if (error.request) throw new Error('Network error: No response from backend');
      throw new Error(error.message || 'Failed to replace asset file');
    }
  }

  /**
   * Detect search type based on input pattern
   * @param query Search query
   * @returns Search type: 'HFN', 'MFA', or 'GENERAL'
   */
  detectSearchType(query: string): 'HFN' | 'MFA' | 'GENERAL' {
    if (!query || typeof query !== 'string') return 'GENERAL';

    const trimmedQuery = query.trim();

    conditionalLog('🔍 [detectSearchType] Query:', `"${trimmedQuery}"`);

    // IMPORTANT: Test MFA first since it's more specific (pure numeric codes)
    // MFA pattern: 5.001.002.002 (NumericLayer.NumericCategory.NumericSubcategory.Sequential) - all numeric
    const mfaRegex = /^\d+\.\d+\.\d+\.\d+$/;
    const isMFA = trimmedQuery.match(mfaRegex);
    conditionalLog(
      '🔍 [detectSearchType] MFA regex test:',
      isMFA ? 'MATCH' : 'NO MATCH'
    );

    if (isMFA) {
      conditionalLog('🎯 [detectSearchType] Detected: MFA');
      return 'MFA';
    }

    // HFN pattern: Supports both simple and composite formats with mixed alphabetic/numeric codes
    // Simple HFN: C.FUL.POP.1041 (Layer.Category.Subcategory.Sequential) - alphabetic codes
    // Mixed HFN: G.001.003.006 (Layer.Category.Subcategory.Sequential) - numeric codes
    // Composite HFN: C.FUL.POP.008:G.001.003.006:S.001.001.001 - mixed formats separated by colons
    // HFN patterns:
    // Simple HFN: G.POP.TEN.001, W.URB.STR.001
    // Composite HFN: C.PAR.4LY.002:1.001.001.001+2.001.001.002+3.001.001.001+4.001.001.001
    // The composite part after colon uses MFA format (numbers only): 1.001.001.001
    // Categories/subcategories can be: pure alphabetic (POP), pure numeric (001), or mixed (4LY)
    const simpleHfnRegex =
      /^[A-Z]\.(?:[A-Z]{3}|[A-Z0-9]{3,})\.(?:[A-Z]{3}|[A-Z0-9]{3,})\.\d+$/;
    const compositeHfnRegex =
      /^C\.(?:[A-Z]{3}|[A-Z0-9]{3,})\.(?:[A-Z]{3}|[A-Z0-9]{3,})\.\d+:\d{1,2}\.\d{3}\.\d{3}\.\d{3}(?:\+\d{1,2}\.\d{3}\.\d{3}\.\d{3})*$/;

    const isSimpleHFN = trimmedQuery.match(simpleHfnRegex);
    const isCompositeHFN = trimmedQuery.match(compositeHfnRegex);
    const isHFN = isSimpleHFN || isCompositeHFN;

    conditionalLog(
      '🔍 [detectSearchType] Simple HFN test:',
      isSimpleHFN ? 'MATCH' : 'NO MATCH'
    );
    conditionalLog(
      '🔍 [detectSearchType] Composite HFN test:',
      isCompositeHFN ? 'MATCH' : 'NO MATCH'
    );
    conditionalLog(
      '🔍 [detectSearchType] HFN regex test:',
      isHFN ? 'MATCH' : 'NO MATCH'
    );

    if (isHFN) {
      conditionalLog('🎯 [detectSearchType] Detected: HFN');
      return 'HFN';
    }

    conditionalLog('🎯 [detectSearchType] Detected: GENERAL');
    return 'GENERAL';
  }

  /**
   * Get asset counts for all layers using the new dedicated endpoint
   * @returns Asset counts by layer
   */
  async getAssetCounts(): Promise<{ byLayer: Record<string, number>; total: number }> {
    try {
      const response = await api.get(`${getBackendUrl()}/assets/count`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      conditionalLog('🔍 [ASSET SERVICE] Raw asset counts response:', response);

      // The backend returns data wrapped in a success/data structure
      if (response.data?.success && response.data?.data) {
        return response.data.data; // Return the inner data object
      }

      // Fallback to direct data if structure is different
      return response.data;
    } catch (error) {
      console.error('Failed to fetch asset counts:', error);
      throw error;
    }
  }
}

const assetService = new AssetService();
export default assetService;
